import { initializeBrowserNotification } from '@/components/BrowserNotification'
import { RenderIf } from '@/components/RenderIf'
import { useNavigation } from '@/hooks/useNavigation'
import { MainLayout } from '@/modules/Activation/components/MainLayout'
import { IUser } from '@/modules/Auth/auth-types'
import { useAuthContext } from '@/modules/Auth/contexts/AuthContext'
import useStyles from '@/modules/Auth/pages/Login/styles'
import { useFirebaseContext } from '@/modules/Firebase/contexts/FirebaseContext'
import { SEND_TO } from '@/modules/Signup/constants'
import { usePatientRegistrationStore } from '@/modules/Signup/store/PatientRegistration.store'
import { AnalyticEvent } from '@/modules/Signup/types/en-us'
import { useThemeGeneratorContext } from '@/theme/contexts/ThemeGeneratorContext'
import { getCache, setCache } from '@/utils/cache'
import { isCPFOrEmail } from '@/utils/validations'
import {
  Button,
  ConexaLogo,
  Input,
  InputPassword,
  Text,
} from '@conexasaude/conexa-hero'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import ReCAPTCHA from 'react-google-recaptcha'
import { useForm } from 'react-hook-form'
import { useLocation, useParams } from 'react-router-dom'
import services from '../../auth-services'
import { WelcomeUserPvModal } from '../../components/WelcomeUserPvModal'
import { type LoginLocation, type LoginParams, LoginFormData } from './types'
import { handleLoginError } from './utils'
import { loginSchema } from './utils/schemas'
import {
  useGetClinicColorsById,
  useGetClinicColorsByUniqueCode,
} from '@/theme/queries'
import { AnimateEnter } from '@/components/Animate'

const Login: FunctionComponent = () => {
  const params = useParams<LoginParams>()
  const location = useLocation<LoginLocation>()
  const navigate = useNavigation()
  const [loading, setLoading] = useState(false)
  const formRef = useRef<HTMLFormElement>(null)
  const form = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  })
  const [imageError, setImageError] = useState(false)
  const recaptchaRef = useRef<ReCAPTCHA>(null)
  const { theme } = useThemeGeneratorContext()
  const { signIn } = useAuthContext()

  const styles = useStyles()

  const { logAnalyticsEvent } = useFirebaseContext()
  const { resetPatientRegistration } = usePatientRegistrationStore()

  const searchParamsInstance = useMemo(() => {
    return new URLSearchParams(location.search)
  }, [location.search])

  const searchParams = useMemo(() => {
    return {
      uniqueCode: searchParamsInstance.get('uniqueCode'),
      clinicId: searchParamsInstance.get('clinicId'),
      originIsPv: searchParamsInstance.get('origem') === 'pv',
    }
  }, [searchParamsInstance])

  const getClinicColorsByUniqueCodeQuery = useGetClinicColorsByUniqueCode(
    searchParams.uniqueCode || params.clinic,
  )

  const getClinicColorsByIdQuery = useGetClinicColorsById(
    Number(searchParams?.clinicId),
  )

  const clinicUniqueCode = useMemo(
    () => params?.clinic || theme?.codigoUnicoClinica,
    [params?.clinic, theme?.codigoUnicoClinica],
  )

  const isLoadingTheme = useMemo(() => {
    return (
      getClinicColorsByUniqueCodeQuery.isFetching ||
      getClinicColorsByIdQuery.isFetching
    )
  }, [
    getClinicColorsByIdQuery.isFetching,
    getClinicColorsByUniqueCodeQuery.isFetching,
  ])

  function handleCloseWelcomeUserPvModal() {
    navigate({
      pathname: '/login',
    })
  }

  const settingUpAnalyticsEvent = useCallback(
    (eventDefinitions: AnalyticEvent) => {
      logAnalyticsEvent('at_patient_login', {
        interaction: eventDefinitions?.interaction,
        options: {
          sendTo: SEND_TO,
        },
      })
    },
    [logAnalyticsEvent],
  )

  const redirectIfAuthenticated = useCallback(
    (pathname?: string) => {
      if (location?.state?.state?.from) {
        navigate(location?.state?.state?.from?.pathname, {
          search: location?.state?.state?.from?.search,
        })
        return
      }

      navigate(pathname)
    },
    [location?.state?.state?.from, navigate],
  )

  useEffect(() => {
    if (getCache('user')) {
      redirectIfAuthenticated('/')
      return
    }

    settingUpAnalyticsEvent({ interaction: 'seen' })
    resetPatientRegistration()
  }, [
    redirectIfAuthenticated,
    resetPatientRegistration,
    settingUpAnalyticsEvent,
  ])

  const login = useCallback(
    async (data: LoginFormData) => {
      try {
        setLoading(true)
        const token = await recaptchaRef?.current?.executeAsync()
        const users: IUser[] = await services.login(
          data?.login,
          data?.password,
          token,
          true,
        )
        const authType = isCPFOrEmail(data?.login)
        signIn(users, authType)
        setLoading(false)
        settingUpAnalyticsEvent({ interaction: 'access_account_success' })
        recaptchaRef?.current?.reset()
        initializeBrowserNotification()

        if (params.clinic) {
          navigate(`/escolha/${params.clinic}`, {
            from: location?.state?.state?.from,
            search: location?.state?.state?.from?.search,
          })
        } else {
          navigate(`/escolha`, {
            from: location?.state?.state?.from,
            search: location?.state?.state?.from?.search,
          })
        }
      } catch (e) {
        recaptchaRef?.current?.reset()
        if (e?.response?.data?.status === 401) {
          form.setError('login', {
            type: 'required',
            message: 'Verifique seu e-mail ou CPF',
          })
          form.setError('password', {
            type: 'required',
            message: 'Verifique sua senha',
          })
        }
        setLoading(false)
        settingUpAnalyticsEvent({ interaction: 'access_account_error' })

        handleLoginError(e, redirectToActivation)
      }
    },
    // eslint-disable-next-line
    [params.clinic, navigate, signIn, logAnalyticsEvent],
  )

  const goToForgotPassword = useCallback(() => {
    logAnalyticsEvent('Pac_Login_EsqueciSenha')

    if (params.clinic) {
      navigate(`/esqueci-senha/${params.clinic}`)
      return
    }

    navigate('/esqueci-senha')
  }, [params.clinic, navigate, logAnalyticsEvent])

  const goToCreateAccount = () => {
    settingUpAnalyticsEvent({ interaction: 'create_account_click' })
    const clinicId = getCache<string>('id-clinic')

    const path = clinicId ? `/cadastro/cpf/${clinicId}` : '/cadastro/cpf'
    navigate(path)
  }

  const redirectToActivation = useCallback(() => {
    if (params?.clinic) setCache('unique-code', String(params?.clinic))

    navigate('/cadastro/cpf')
  }, [navigate, params.clinic])

  return (
    <MainLayout
      hideMobileHeader
      tagEventName="at_patient_login"
      isLoading={isLoadingTheme}
    >
      <div css={styles.container}>
        <div css={styles.loginForm}>
          <RenderIf condition={!isLoadingTheme}>
            <AnimateEnter>
              <RenderIf condition={!!clinicUniqueCode && !imageError}>
                <img
                  src={`${
                    import.meta.env.VITE_APP_LINK_LOGO_AWS
                  }${clinicUniqueCode}.png`}
                  alt="logo"
                  css={styles.logoWhiteLabel}
                  onError={event => {
                    event.currentTarget.onerror = null
                    setImageError(true)
                    event.currentTarget.style.height = 'auto'
                  }}
                />
              </RenderIf>
              <RenderIf condition={!clinicUniqueCode || imageError}>
                <ConexaLogo base="colorful" width={150} height={32.35} />
              </RenderIf>
            </AnimateEnter>
          </RenderIf>

          <Text variant="subheading" css={styles.subtitleLoginInstructions}>
            Utilize o seu e-mail ou CPF e senha cadastrada para entrar.
          </Text>

          <form
            ref={formRef}
            autoComplete="off"
            onSubmit={form.handleSubmit(login)}
          >
            <div css={styles.containerFirstInput}>
              <Input
                {...form.register('login')}
                label="E-mail ou CPF"
                placeholder="Digite o seu e-mail ou número do seu CPF"
                fullWidth
                error={form.formState.errors.login?.message?.toString()}
              />
            </div>
            <InputPassword
              label="Senha"
              placeholder="Digite a sua senha"
              fullWidth
              {...form.register('password')}
              error={form.formState.errors.password?.message?.toString()}
            />
            <div css={styles.linkLogin}>
              <Text
                renderAs="span"
                variant="linkUnderline"
                onClick={goToForgotPassword}
              >
                Esqueceu sua senha?
              </Text>
            </div>
            <div css={styles.buttonsWrapper}>
              <Button
                busy={loading}
                fullWidth
                id="at_patient_login_access_account"
              >
                Acessar conta
              </Button>
              <Button
                fullWidth
                id="at_patient_login_create_account"
                variant="secondary"
                onClick={goToCreateAccount}
                type="button"
              >
                Criar conta
              </Button>
            </div>

            <ReCAPTCHA
              ref={recaptchaRef}
              size="invisible"
              sitekey={import.meta.env.VITE_APP_RECAPTCHA_SITE_KEY}
            />
          </form>
        </div>
      </div>

      <WelcomeUserPvModal
        opened={searchParams.originIsPv}
        onClose={handleCloseWelcomeUserPvModal}
      />
    </MainLayout>
  )
}

export default Login
